import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PickCard from 'components/PickCard/PickCard';
import Spinner from 'components/Spinner/Spinner';
import Button from 'components/Button/Button';
import { getPickOfDay } from 'services/picksApi';
import { listGames } from 'services/gamesApi';
import { GROUP_NAMES } from 'utils';
import type { ApiPick, ApiGame, ApiGameListResponse } from 'services/types';
import { t } from '../../helpers/componentHelper';

import './TodaysPick.scss';

type TodaysPickComponentProps = {
  userGroups: string[];
};

// Top-level TodaysPick page
function TodaysPick({ userGroups }: TodaysPickComponentProps) {
  const navigate = useNavigate();
  const [picks, setPicks] = useState<ApiPick[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // only allow access to this page for Elite users
  useEffect(() => {
    if (userGroups.length > 0 && !userGroups.includes(GROUP_NAMES.elite)) {
      navigate('/picks');
    }
  }, [userGroups, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPickOfDay()
      .then(async (pickRes) => {
        if (cancelled) return;
        if (pickRes.length === 0) {
          setPicks([]);
          setError(`${t('pages.todaysPick.headerText')}`);
          return;
        }
        // Resolve eventName per unique sport in the response — a single
        // listGames call per sport is cheap and reused across picks.
        const sports = Array.from(new Set(pickRes.map((p) => p.sport)));
        const gameLookup = new Map<string, ApiGame>();
        await Promise.all(
          sports.map(async (sport) => {
            const res: ApiGameListResponse = await listGames({ sport, pageNum: 0 });
            for (const game of res.results) gameLookup.set(game.eventId, game);
          }),
        );
        if (cancelled) return;
        setPicks(
          pickRes.map((p) => {
            const eventName =
              gameLookup.get(p.eventId)?.eventName.replace('Athletics Athletics', 'Athletics') ??
              '';
            return { ...p, eventName };
          }),
        );
        setError(null);
      })
      .catch(() => {
        setError(`${t('pages.ourBet.noResults')}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={'todaysPick'}>
      <div className={'todaysPick__header'}>
        <div>{`${t('pages.todaysPick.headerText')}`}</div>
        <div className={'todaysPick__header--subtext'}>{`${t(
          'pages.todaysPick.headerSubtext',
        )}`}</div>
      </div>

      <div className={'todaysPick__meta'}>
        {loading && picks.length === 0 ? (
          <div className={'todaysPick-detail__loading'}>
            <Spinner />
          </div>
        ) : null}
      </div>

      {error && (
        <div className={'todaysPick__error'} role={'alert'}>
          {error}
        </div>
      )}

      <div className={'todaysPick__list'}>
        {picks.map((pick) => (
          <PickCard key={pick.pickId} pick={pick} userGroups={userGroups} />
        ))}

        <div className={'todaysPick__button-container'}>
          <Button
            text={`${t('pages.todaysPick.buttonText')}`}
            variant="default"
            active={true}
            size="large"
            onClick={() => navigate('/picks')}
          />
        </div>
      </div>
    </div>
  );
}

export default TodaysPick;
